import React from "react";
import { Alert, Modal, Tag } from "antd";
import { OVERLAP_STYLE } from "./offsetOverlapDetection";
import {
  formatCarLabel,
  formatContendedBytes,
  formatRegisterRange,
  scopeLabel,
  summarizeOverlapReport,
  NOTHING_CHECKED_WARNING,
  SINGLE_CAR_WARNING,
  type OverlapFinding,
  type OverlapReport,
} from "./overlapReportModel";

/**
 * OverlapAnalysisModal — the Overlap Analysis report, in this window.
 *
 * The structure is deliberately the same two levels the printable document
 * renders: one block per colliding pair of registers, and inside it one row per
 * concrete collision. The two middle column headings ARE the message names, so
 * each row carries only what changes between collisions — the car and the
 * offset — instead of repeating the message on every line.
 *
 * It renders no `Button` of its own; antd's own close control shuts it. That is
 * not an aesthetic choice: every `Button` in this render tree becomes a row in
 * the generated control inventory that somebody then has to write prose for,
 * and a report has nothing for a button to do.
 */
export interface OverlapAnalysisModalProps {
  /** Whether the report panel is showing. */
  open: boolean;
  /** Called when the operator dismisses the panel. */
  onClose: () => void;
  /** The report to render; `null` before the button has ever been pressed. */
  report: OverlapReport | null;
  /** The adapter this report is about, as named in the form. */
  adapterName: string;
  /** The vendor protocol the adapter speaks. */
  protocolType: string;
}

const CELL: React.CSSProperties = {
  border: "1px solid #d9d9d9",
  padding: "4px 8px",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const HEAD_CELL: React.CSSProperties = { ...CELL, fontWeight: 600 };

const FINDING_BOX: React.CSSProperties = {
  border: "1px solid #d9d9d9",
  borderRadius: 4,
  padding: "12px 16px",
  marginBottom: 16,
};

/**
 * A cross-car finding wears the same red glow the matrix paints on the cells
 * it comes from, so the report and the highlighted cell read as one system.
 */
const CROSS_CAR_BOX: React.CSSProperties = {
  ...FINDING_BOX,
  border: "1px solid",
  ...OVERLAP_STYLE,
  padding: "12px 16px",
  marginBottom: 16,
};

/** One finding: the pair, its counts, and every collision between them. */
function FindingBlock({ finding }: { finding: OverlapFinding }): React.ReactElement {
  const overlaps = finding.occurrences.length === 1 ? "overlap" : "overlaps";
  return (
    <section
      data-testid="overlap-finding"
      style={finding.hasCrossUnit ? CROSS_CAR_BOX : FINDING_BOX}
    >
      <div style={{ marginBottom: 4 }}>
        {finding.hasCrossUnit ? (
          <Tag color="red">CROSS-CAR</Tag>
        ) : (
          <Tag color="orange">SAME CAR</Tag>
        )}
        <span style={{ fontWeight: 600 }}>
          {finding.aLabel} &harr; {finding.bLabel}
        </span>
      </div>
      <div style={{ opacity: 0.75, marginBottom: 10 }}>
        {`${finding.occurrences.length} ${overlaps}, ${finding.crossUnitCount} cross-car, `}
        {`first contended byte ${finding.firstContendedByte}`}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th style={HEAD_CELL}>Scope</th>
              <th style={HEAD_CELL}>{finding.aLabel}</th>
              <th style={HEAD_CELL}>{finding.bLabel}</th>
              <th style={HEAD_CELL}>Contended bytes</th>
            </tr>
          </thead>
          <tbody>
            {finding.occurrences.map((occurrence) => (
              <tr
                data-testid="overlap-occurrence"
                key={`${occurrence.unitAIdx}-${occurrence.unitBIdx}-${occurrence.bytes[0] ?? 0}`}
              >
                <td
                  data-testid="overlap-scope"
                  style={
                    occurrence.crossUnit
                      ? { ...CELL, color: OVERLAP_STYLE.borderColor, fontWeight: 700 }
                      : CELL
                  }
                >
                  {scopeLabel(occurrence)}
                </td>
                <td style={CELL}>
                  {`${formatCarLabel(occurrence.carA)} ${formatRegisterRange(
                    occurrence.aStart,
                    occurrence.aEnd,
                  )}`}
                </td>
                <td style={CELL}>
                  {`${formatCarLabel(occurrence.carB)} ${formatRegisterRange(
                    occurrence.bStart,
                    occurrence.bEnd,
                  )}`}
                </td>
                <td style={CELL}>{formatContendedBytes(occurrence.bytes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * The report panel. Renders nothing at all until a report has been built.
 *
 * Unlike the printable window this panel is LIVE: it is handed the same report
 * the form recomputes as offsets are edited, so it can never describe an
 * adapter that has since changed. That is the reason it carries no "snapshot"
 * time, and the reason it is the default destination.
 */
export default function OverlapAnalysisModal({
  open,
  onClose,
  report,
  adapterName,
  protocolType,
}: OverlapAnalysisModalProps): React.ReactElement | null {
  if (!report) return null;

  return (
    <Modal
      data-testid="overlap-analysis-modal"
      open={open}
      onCancel={onClose}
      footer={null}
      width={980}
      title={
        <div>
          <div>{`Overlap analysis - ${adapterName}`}</div>
          <div style={{ fontWeight: 400, opacity: 0.75, fontSize: 12 }}>{protocolType}</div>
        </div>
      }
    >
      <div data-testid="overlap-analysis-summary" style={{ fontWeight: 600, marginBottom: 16 }}>
        {summarizeOverlapReport(report)}
      </div>
      {report.columnsChecked === 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={NOTHING_CHECKED_WARNING}
          data-testid="overlap-nothing-checked-warning"
        />
      )}
      {report.columnsChecked > 0 && report.carsAnalyzed < 2 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={SINGLE_CAR_WARNING}
          data-testid="overlap-single-car-warning"
        />
      )}
      {report.scopeNote !== null && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={report.scopeNote}
          data-testid="overlap-scope-note"
        />
      )}
      {report.findings.map((finding) => (
        <FindingBlock key={`${finding.aColKey}|${finding.bColKey}`} finding={finding} />
      ))}
    </Modal>
  );
}
